/**
 * TransportsCard — arrêts de transport en commun proches.
 *
 * UX : 4 compteurs (métro / tram / train / bus) cliquables comme tabs,
 * puis top 8 de la catégorie active. Les lignes desservies sont affichées
 * à droite du nom de l'arrêt (pills compactes).
 */

import { useEffect, useState } from "react";
import type { FicheDoc } from "../../lib/fiches";
import {
  useTransports,
  type TransportMode,
  type TransportItem,
  type TransportsData,
} from "../../lib/env/transports";
import { TIER_LABEL } from "../../lib/env/localityTier";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

// Mode ordering = perceived "priority" for a house shopper: a metro stop
// is a bigger signal than a bus stop, so default the active tab to the
// richest category actually present (cf. `defaultMode` below).
const MODE_ORDER: {
  key: TransportMode;
  label: string;
  short: string;
  tone: string;
}[] = [
  { key: "metro", label: "Métro",      short: "Métro",  tone: "bg-indigo-100 text-indigo-800" },
  { key: "tram",  label: "Tram",       short: "Tram",   tone: "bg-rose-100 text-rose-800" },
  { key: "train", label: "Train / RER",short: "Train",  tone: "bg-sky-100 text-sky-800" },
  { key: "bus",   label: "Bus",        short: "Bus",    tone: "bg-emerald-100 text-emerald-800" },
];

export function TransportsCard({ fiche }: Props) {
  const state = useTransports(fiche.id, fiche.geocode);

  // Initial tab is "metro" at mount, but we switch it to the highest-signal
  // mode *actually present* once data arrives — so a rural fiche with
  // nothing but bus stops doesn't open on an empty Métro tab.
  const [tab, setTab] = useState<TransportMode>("metro");
  const [userPicked, setUserPicked] = useState(false);
  useEffect(() => {
    if (userPicked) return;
    if (state.status !== "ready") return;
    const best = defaultMode(state.data.counts);
    setTab(best);
  }, [state, userPicked]);
  const onTab = (m: TransportMode) => {
    setUserPicked(true);
    setTab(m);
  };

  const subtitle = state.status === "ready" ? summarise(state.data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Compass size={18} />}
      title="Transports & mobilité"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : base consolidée nationale des arrêts (transport.data.gouv.fr,
          agrégation des GTFS officiels des AO). Fallback OpenStreetMap si la
          cellule nationale est indisponible.
        </>
      }
    >
      {state.status === "ready" && (
        <TransportsBody
          counts={state.data.counts}
          items={state.data.items}
          active={tab}
          onTab={onTab}
        />
      )}
    </EnvCardShell>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────

function TransportsBody({
  counts,
  items,
  active,
  onTab,
}: {
  counts: Record<TransportMode, number>;
  items: TransportItem[];
  active: TransportMode;
  onTab: (m: TransportMode) => void;
}) {
  const activeItems = items.filter((it) => it.mode === active).slice(0, 8);

  return (
    <div>
      <div className="grid grid-cols-4 gap-1.5">
        {MODE_ORDER.map(({ key, short }) => {
          const selected = active === key;
          const n = counts[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTab(key)}
              disabled={n === 0}
              className={[
                "rounded-xl px-2.5 py-2 text-left transition-colors",
                selected
                  ? "bg-ink-900 text-paper"
                  : n === 0
                  ? "bg-ink-50 text-ink-400 hairline cursor-not-allowed"
                  : "bg-ink-50 text-ink-800 hover:bg-ink-100 hairline",
              ].join(" ")}
            >
              <div className="text-[10.5px] uppercase tracking-[0.08em] opacity-80">
                {short}
              </div>
              <div className="text-[20px] font-semibold tabular leading-tight mt-0.5">
                {n}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {activeItems.length === 0 ? (
          <div className="text-[12.5px] text-ink-500 py-2">
            Aucun arrêt de cette catégorie à proximité.
          </div>
        ) : (
          <ul className="space-y-1">
            {activeItems.map((it) => (
              <TransportRow key={it.id} item={it} />
            ))}
            {counts[active] > activeItems.length && (
              <li className="text-[11.5px] text-ink-500 pl-0.5">
                + {counts[active] - activeItems.length} autres
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function TransportRow({ item }: { item: TransportItem }) {
  const tone = MODE_ORDER.find((m) => m.key === item.mode)?.tone ?? "";
  return (
    <li className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink-900 truncate">{item.name}</div>
        {item.lines.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {item.lines.map((l) => (
              <span
                key={l}
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${tone}`}
              >
                {l}
              </span>
            ))}
          </div>
        )}
      </div>
      <span className="text-[11.5px] text-ink-500 tabular shrink-0 w-12 text-right">
        {formatDistance(item.distance_m)}
      </span>
    </li>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function summarise(d: TransportsData): string {
  const radiusLabel = `rayon ${formatRadius(d.radius_m)} · ${TIER_LABEL[d.tier]}`;
  const total = d.counts.metro + d.counts.tram + d.counts.train + d.counts.bus;
  if (total === 0) {
    return `Aucun arrêt · ${radiusLabel}`;
  }
  const parts: string[] = [];
  if (d.counts.metro) parts.push(`${d.counts.metro} métro`);
  if (d.counts.tram) parts.push(`${d.counts.tram} tram`);
  if (d.counts.train) parts.push(`${d.counts.train} train`);
  if (d.counts.bus) parts.push(`${d.counts.bus} bus`);
  parts.push(radiusLabel);
  return parts.join(" · ");
}

function defaultMode(counts: Record<TransportMode, number>): TransportMode {
  // Prefer the higher-signal mode when counts are non-zero; fall through
  // to bus when nothing else is available.
  if (counts.metro > 0) return "metro";
  if (counts.tram > 0) return "tram";
  if (counts.train > 0) return "train";
  return "bus";
}

function formatDistance(m: number): string {
  if (m < 950) return `${m} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

function formatRadius(m: number): string {
  if (m < 1000) return `${m} m`;
  if (m % 1000 === 0) return `${m / 1000} km`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}
